import React from 'react';
import { Modal, Button, Empty, Table, Tooltip, Spin, Collapse, Tabs } from 'antd';
import { MessageOutlined, LineChartOutlined, PlayCircleOutlined } from '@ant-design/icons';
import { useAppDispatch, useAppSelector, useSessionTimeline } from 'shared/hooks';
import { closeModal } from 'services/store/reducers/modalReducer';
import { SessionVrUser, AnswerReports, ProgressReports, ChatReports, TableSession } from 'shared/types';
import { UserField } from './components';
import { SessionTimelineView, SessionRawTables } from 'shared/components/session-clock';
import Text from 'antd/lib/typography/Text';
import {
  downloadAnswerReports,
  downloadProgressReports,
  downloadChatReports
} from 'services/api/sessionsService';
import { formatTime, parseISODateFormat } from 'utils/date-utils';

type Props = {
  usersList: SessionVrUser[];
  session: TableSession;
  sessionId?: string;
  internalId?: string;
  answerReports?: AnswerReports[];
  progressReports?: ProgressReports[];
  chatReports?: ChatReports[];
  onReassigned?: () => void;
};

const AttendeesModal: React.FC<Props> = ({
  usersList,
  sessionId,
  internalId,
  answerReports,
  progressReports,
  chatReports,
  session,
  onReassigned,
}): JSX.Element => {
  const dispatch = useAppDispatch();
  const isModalOpened = useAppSelector((state): boolean => state.modal.isModalOpened);
  const { timeline, isLoading: isTimelineLoading } = useSessionTimeline(sessionId);

  const handleCancel = (): void => {
    dispatch(closeModal());
  };

  const userLabel = (u: { first_name: string | null; last_name: string | null; email: string | null; id: string }): string => {
    const name = `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim();
    return name || u.email || u.id;
  };

  const showAnswers = !!answerReports && !!answerReports.length;
  const showProgress = !!progressReports && !!progressReports.length;
  const showChatLogs = !!chatReports && !!chatReports.length;
  const showDowloadReports = (showAnswers || showProgress || showChatLogs) && sessionId;

  const progressRows = (progressReports ?? []).map((r: any, i: number) => ({
    key: r.id ?? i,
    leName: r.learningExperienceName ?? r.learning_experiences?.display_name ?? r.learning_experiences?.name ?? '—',
    lastStation: r.lastCompletedStation ?? r.last_completed_station,
    timeInStation: r.timeSpentInStation ?? r.time_in_station_seconds,
    totalStations: r.totalStationCount ?? r.learning_experiences?.station_count,
    timeInLE: r.timeSpentInLe ?? r.time_in_learning_experience_seconds,
  }));

  const progressColumns = [
    { title: 'LE Name', dataIndex: 'leName', key: 'leName', ellipsis: true },
    {
      title: 'Last Station',
      dataIndex: 'lastStation',
      key: 'lastStation',
      width: 110,
      render: (v: number | null) => v != null ? v : 'Intro',
    },
    {
      title: 'Time in Station',
      dataIndex: 'timeInStation',
      key: 'timeInStation',
      width: 125,
      render: (v: number | null) => v != null ? formatTime(v) : '—',
    },
    {
      title: 'Total Stations',
      dataIndex: 'totalStations',
      key: 'totalStations',
      width: 120,
      render: (v: number | null) => v ?? '—',
    },
    {
      title: 'Time in LE',
      dataIndex: 'timeInLE',
      key: 'timeInLE',
      width: 100,
      render: (v: number | null) => v != null ? formatTime(v) : '—',
    },
  ];

  return (
    <Modal
      open={isModalOpened}
      title='Session details'
      onCancel={handleCancel}
      footer={[
        <Button key='cancel' type={'primary'} onClick={handleCancel}>
          Close
        </Button>
      ]}
      width={'1000px'}
    >
      {!!sessionId && (
        <Text strong>
          <p style={{ marginBottom: 0 }}>{`Session ID: ${sessionId}`}</p>
        </Text>
      )}
      {!!(timeline?.sessionStart ?? session?.startedAt) && (
        <p style={{ color: '#8c8c8c', marginTop: 2 }}>
          {parseISODateFormat(timeline?.sessionStart ?? session?.startedAt, 'MMMM Do YYYY, h:mma')}
        </p>
      )}

      {isTimelineLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '32px 0' }}>
          <Spin />
        </div>
      ) : timeline ? (
        <div style={{ marginBottom: 16 }}>
          {timeline.users.length > 1 ? (
            <Tabs
              items={timeline.users.map(u => ({
                key: u.id,
                label: userLabel(u),
                children: <SessionTimelineView timeline={timeline} userId={u.id} />,
              }))}
            />
          ) : (
            <SessionTimelineView timeline={timeline} userId={timeline.users[0]?.id ?? null} />
          )}
        </div>
      ) : null}

      {showDowloadReports && (
        <div>
          <Text strong>Download reports</Text>
          {showAnswers && (
            <Tooltip title='Answers'>
              <Button
                size='small'
                icon={<MessageOutlined />}
                style={{ marginLeft: 8 }}
                onClick={() => downloadAnswerReports(session)}
              />
            </Tooltip>
          )}
          {showProgress && (
            <Tooltip title='Progress'>
              <Button
                size='small'
                icon={<LineChartOutlined />}
                style={{ marginLeft: 8 }}
                onClick={() => downloadProgressReports(session)}
              />
            </Tooltip>
          )}
          {showChatLogs && (
            <Tooltip title='What users said'>
              <Button
                size='small'
                icon={<PlayCircleOutlined />}
                style={{ marginLeft: 8 }}
                onClick={() => downloadChatReports(sessionId, chatReports)}
              />
            </Tooltip>
          )}
        </div>
      )}
      {(timeline || progressRows.length > 0) && (
        <Collapse
          style={{ marginTop: 16 }}
          items={[
            {
              key: 'raw',
              label: 'Raw logs (LEs · stations · progress · answers)',
              children: timeline ? (
                <SessionRawTables timeline={timeline} />
              ) : (
                <Table
                  size='small'
                  columns={progressColumns}
                  dataSource={progressRows}
                  pagination={false}
                />
              ),
            },
          ]}
        />
      )}
      {!!usersList.length ? (
        usersList.map((user): JSX.Element => <UserField user={user} session={session} key={user.id} onReassigned={onReassigned} />)
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description='No users were found for this session'
        />
      )}
    </Modal>
  );
};

export default AttendeesModal;
